import type { MouseEvent } from 'react'
import { legacyContractDataAttrs } from '@/msgdeskRuntime/legacyBridge/SelectorContracts'
import { dispatchDeskNavigation, type DeskViewMode } from '@/msgdeskRuntime/deskNavigation'
import { dispatchEditorAction } from '@/msgdeskRuntime/editorActions'
import type { EditorMode } from '@/msgdeskRuntime/contracts/editorActions'
import { AuthEntry } from '@/deskUiSkeletons/Shared/AuthEntry'
import { DeskStateBadge } from '@/deskUiSkeletons/Shared/DeskStateBadge'
import { ParentsMenu } from '@/deskUiSkeletons/Menu/ParentsMenu'
import { useDeskMenu } from '@/deskUiSkeletons/Menu/menuModel'

// PoO wording for the shared menu-model entry vocabulary.
const labels: Record<DeskViewMode, string> = {
  msg: 'Document',
  answers: 'Answers',
  submsges: 'Messages',
  attachments: 'Files',
  favs: 'Favorites',
  drafts: 'Drafts',
  trash: 'Trash',
}

export function PooMenu({ isEditorOpen, editorMode }: { isEditorOpen: boolean; editorMode?: EditorMode }) {
  const menu = useDeskMenu()
  const canAddAnswer = menu.can.reply
  const canAddMessage = menu.can.createMessage
  // LOCKED-SCREEN: a show/time-locked desk shows ONLY the locked template.
  // Keep sidebar.root MOUNTED (legacy address) but hidden.
  const locked = !menu.access.showUnlocked

  function navigate(event: MouseEvent<HTMLButtonElement>, mode: DeskViewMode) {
    dispatchDeskNavigation({ mode, target: event.currentTarget })
  }

  return (
    <nav
      {...legacyContractDataAttrs('sidebar.root')}
      className={`fixed inset-x-2 top-2 z-[100000] mx-auto flex h-11 max-w-[calc(100vw-16px)] items-center gap-1 overflow-visible rounded-lg border border-[#d7d1c5] bg-white/95 p-1 text-xs text-[#58635f] shadow-lg backdrop-blur lg:max-w-max${locked ? ' hidden' : ''}`}
      style={locked ? { display: 'none' } : undefined}
      aria-label="Opening Proof Protocol controls"
    >
      <span className="flex h-9 flex-none items-center rounded-md bg-[#173f35] px-3 font-semibold tracking-wide text-white">OPP</span>
      <ParentsMenu skin={{
        root: 'relative flex h-9 flex-none items-center',
        trigger: 'flex h-9 cursor-pointer items-center gap-1 rounded-md px-3 text-xs font-medium text-[#58635f] hover:bg-[#f0ede6] hover:text-[#17211e]',
        triggerOpen: 'bg-[#e1ebe7] text-[#173f35]',
        count: 'text-[10px] text-[#8a938f]',
        list: 'absolute left-0 top-full z-[100001] mt-1 flex min-w-[200px] max-w-[90vw] flex-col gap-0.5 rounded-lg border border-[#d7d1c5] bg-white/95 p-2 text-xs shadow-lg backdrop-blur',
        link: 'truncate rounded-md px-3 py-1.5 text-[#245f51] no-underline hover:bg-[#edf4f1] hover:underline',
        homeLink: 'mt-0.5 truncate rounded-md border-t border-[#d7d1c5] px-3 py-1.5 pt-2 text-[#58635f] no-underline hover:bg-[#f0ede6]',
      }} />
      <div className="flex min-w-0 flex-1 items-center gap-1 overflow-x-auto">
        {isEditorOpen ? (
          <>
            <span className="flex-none px-2 font-medium text-[#25302d]">{editorMode === 'update_msg' ? 'Edit document' : 'New document'}</span>
            <button type="button" className="h-9 flex-none rounded-md px-3 text-[#8a352b] hover:bg-[#f8ece9]" onClick={event => navigate(event, 'msg')}>Close</button>
          </>
        ) : (
          <>
            {/* FAVS-IDENTITY: no Favorites tab for anonymous viewers of an edit-locked desk.
                §0.16: model-unavailable entries (Messages on an answer desk) never render. */}
            {menu.tabs.filter(tab => tab.available && tab.hasItems && (!tab.requiresEdit || menu.access.editUnlocked) && (tab.mode !== 'favs' || menu.can.favs)).map(tab => (
              <button
                key={tab.mode}
                type="button"
                className={`h-9 flex-none rounded-md px-3 font-medium ${tab.isActive ? 'bg-[#e1ebe7] text-[#173f35]' : 'hover:bg-[#f0ede6] hover:text-[#17211e]'}`}
                aria-current={tab.isActive ? 'page' : undefined}
                onClick={event => navigate(event, tab.mode)}
              >
                {labels[tab.mode]}
              </button>
            ))}
            {canAddAnswer ? (
              <button type="button" className="h-9 flex-none rounded-md bg-[#edf4f1] px-3 font-semibold text-[#245f51] hover:bg-[#dceae5]" onClick={() => dispatchEditorAction({ action: 'open-reply' })}>
                + Answer
              </button>
            ) : null}
            {canAddMessage ? (
              <button type="button" className="h-9 flex-none rounded-md bg-[#edf4f1] px-3 font-semibold text-[#245f51] hover:bg-[#dceae5]" onClick={() => dispatchEditorAction({ action: 'open-new-message' })}>
                + Message
              </button>
            ) : null}
          </>
        )}
      </div>
      <span className="ml-auto flex flex-none items-center gap-1">
        {/* POSSESSION-MODEL: desk state before the identity; identity keeps
            the bar's extreme right (= page top-right). */}
        <DeskStateBadge skin={{
          root: 'flex items-center whitespace-nowrap px-2 text-[11px] font-medium',
          possessedOpen: 'text-[#245f51]',
          possessedPin: 'text-[#245f51]',
          locked: 'text-[#8b9490]',
          label: 'cursor-pointer border-0 bg-transparent p-0 text-inherit underline decoration-dotted underline-offset-2',
          form: 'ml-1.5 inline-flex items-center gap-1',
          input: 'h-[26px] w-[110px] rounded-md border border-[#d5d0c4] bg-white px-2 text-[12px] text-[#17211e] outline-none',
          button: 'h-[26px] cursor-pointer rounded-md px-2 text-[12px] text-[#245f51] hover:bg-[#f0ede6]',
        }} />
        <AuthEntry skin={{
          root: 'flex items-center gap-1',
          signInButton: 'h-9 flex-none rounded-md px-3 font-medium hover:bg-[#f0ede6] hover:text-[#17211e]',
          identity: 'flex items-center gap-1',
          identityLink: 'font-semibold text-[#245f51] hover:underline max-w-[110px] overflow-hidden text-ellipsis whitespace-nowrap',
          signOutButton: 'h-9 flex-none rounded-md px-2 hover:bg-[#f0ede6]',
        }} />
      </span>
    </nav>
  )
}
